import { ConnectProto } from "@racoon/api";
import type { ContainerTab } from "./useContainerUrl";

const { ContainerStatus, ContainerTrackingEvent } = ConnectProto;
type Container = ConnectProto.Container;

/**
 * What the table and the filter sheet call a container's "status": the archive
 * flag folded together with the last tracking event, since that is how the
 * dispatcher thinks about a container ("on site", "in the fleet", "archived").
 * The same three values are the tabs above the table, minus "active".
 */
export type ContainerViewStatus = Exclude<ContainerTab, "active">;

export const containerViewStatus = (container: Container): ContainerViewStatus | undefined => {
  if (container.status === ContainerStatus.INACTIVE) return "archived";
  switch (container.lastEvent) {
    case ContainerTrackingEvent.DELIVERED:
      return "delivered";
    case ContainerTrackingEvent.COLLECTED:
      return "collected";
    default:
      // Never reported by a driver — the table shows no chip for it.
      return undefined;
  }
};

/**
 * The pickup location the table shows for a container: the one it is
 * registered at, falling back to where its last job was. Every driver report
 * rewrites the registered location, and so does a manual edit in the dialog —
 * which leaves the last job untouched, so preferring the job's site would keep
 * showing the old one after an edit. It is also the location the sheet's
 * server-side site filter matches.
 *
 * Both are preloaded by ListContainers, so nothing here resolves an id against
 * a separately fetched location list.
 */
export const lastKnownPickupLocation = (
  container: Container,
): ConnectProto.PickupLocation | undefined =>
  container.pickupLocation ?? container.lastJob?.pickupLocation;

/** The driver of the container's last job; undefined when the job carries no shift. */
export const lastJobDriver = (container: Container): ConnectProto.User | undefined =>
  container.lastJob?.driverOnShift?.driver;

const hasFileReport = (job: ConnectProto.Job, type: ConnectProto.FileUploadType) =>
  (job.fileReports ?? []).some((file) => file.type === type);

/**
 * Sort key for the photo column: 0 — no last job, 1 — a job without a full
 * report, 2 — both the "service started" and "service ended" photos exist
 * (the same rule the photo-report icon uses).
 */
export const lastJobPhotoRank = (container: Container): number => {
  const job = container.lastJob;
  if (!job) return 0;
  const hasStarted =
    Boolean(job.serviceStartedReportUrl) ||
    hasFileReport(job, ConnectProto.FileUploadType.JOB_SERVICE_STARTED);
  const hasEnded =
    Boolean(job.serviceEndedReportUrl) ||
    hasFileReport(job, ConnectProto.FileUploadType.JOB_SERVICE_ENDED);
  return hasStarted && hasEnded ? 2 : 1;
};

export const driverFullName = (driver: ConnectProto.User): string =>
  [driver.lastName, driver.firstName].filter(Boolean).join(" ").trim() || `#${driver.id}`;

/**
 * The ListContainers filters the table's request and the tab badges' request
 * share. The page builds them once and hands the same object to both, so a
 * tab's badge counts exactly the rows that tab's request returns.
 */
export type ContainerServerFilters = Pick<
  ConnectProto.ContainerListRequest,
  "ownerOrganizationIds" | "containerTypeIds" | "pickupLocationIds" | "driverIds"
> &
  Partial<Pick<ConnectProto.ContainerListRequest, "search">>;

/**
 * What a tab asks ListContainers for. The tab is a server-side filter: "active"
 * is every ACTIVE container, the two event tabs narrow ACTIVE by the last
 * tracking event, "archived" is INACTIVE.
 */
export const tabListRequest = (
  tab: ContainerTab,
): Pick<ConnectProto.ContainerListRequest, "statuses"> &
  Partial<Pick<ConnectProto.ContainerListRequest, "lastEvents">> => {
  switch (tab) {
    case "archived":
      return { statuses: [ContainerStatus.INACTIVE] };
    case "delivered":
      return { statuses: [ContainerStatus.ACTIVE], lastEvents: [ContainerTrackingEvent.DELIVERED] };
    case "collected":
      return { statuses: [ContainerStatus.ACTIVE], lastEvents: [ContainerTrackingEvent.COLLECTED] };
    case "active":
      return { statuses: [ContainerStatus.ACTIVE] };
  }
};

/**
 * The badge on each tab, read off the facets of one ListContainers response
 * whose request asks for `statuses: [ACTIVE]` and the "status" and "last_event"
 * facets.
 *
 * Each facet is counted under every filter but its own: "status" ignores the
 * statuses filter, so it answers the two status tabs across all containers,
 * while "last_event" keeps it — ACTIVE containers only — and answers the two
 * event tabs. Selecting a tab therefore does not zero the other badges. A
 * missing count means the counts have not arrived yet: leave the badge off
 * rather than show a stale 0.
 */
export const tabCounts = (
  listInfo: ConnectProto.ListInfo | undefined,
): Partial<Record<ContainerTab, number>> => {
  const byStatus = listInfo?.facets["status"]?.counts;
  const byEvent = listInfo?.facets["last_event"]?.counts;
  return {
    active: byStatus?.[ContainerStatus.ACTIVE],
    archived: byStatus?.[ContainerStatus.INACTIVE],
    delivered: byEvent?.[ContainerTrackingEvent.DELIVERED],
    collected: byEvent?.[ContainerTrackingEvent.COLLECTED],
  };
};

/**
 * ListContainers' `sort_by` for each column of the table. The server orders by the
 * same value the column shows; column ids are kept from the old layout so saved
 * column configs keep applying.
 */
export const CONTAINER_SORT_BY: Readonly<Record<string, string>> = {
  name: "name",
  status: "status",
  locationId: "pickup_location",
  containerType: "container_type",
  volume: "volume",
  updatedAt: "updated_at",
  driver: "driver",
  lastJob: "photo",
};
